import { format, addDays, differenceInWeeks, nextMonday, isMonday, isBefore } from "date-fns";
import { generateDaysArray } from "./shared/calendarUtils";
import {
    PSY_HOURLY_SLOTS,
    NFB_WEEKDAY_SLOTS,
    NFB_SATURDAY_SLOTS,
    WEEK_DAY_SHORT_LABELS,
    DAYS_WINDOW,
    SATURDAY_WEEKDAY,
} from "./constants";
import { AvailableSlot, FutureAvailableSlot, FindAvailableSlotsResult, ServiceAvailability } from "./types";

type Service = "psy" | "nfb";
type AvailabilityStatus = "available" | "time_off" | null;

type FindAvailableSlotsParams = {
    startDate: Date;
    includePsy: boolean;
    includeNfb: boolean;
    getOccupancy: (dateKey: string, time: string, service: Service) => number;
    getNfbCapacity: (dateKey: string, time: string) => number;
    getAvailabilityStatus: (dateKey: string, time: string, service: Service) => AvailabilityStatus;
};

type SlotChecker = {
    service: Service;
    capacityFor: (dateKey: string, time: string) => number;
    getOccupancy: (dateKey: string, time: string, service: Service) => number;
    getAvailabilityStatus: (dateKey: string, time: string, service: Service) => AvailabilityStatus;
};

type DayWithWeekInfo = {
    dateKey: string;
    weekDay: number;
    weekIndex: number;
};

const generateDaysWithWeekIndex = (startDate: Date, count: number): Array<DayWithWeekInfo> => {
    const referenceMonday = isMonday(startDate) ? startDate : nextMonday(startDate);
    const daysWithWeekIndex: Array<DayWithWeekInfo> = generateDaysArray(startDate, count).map((day) => ({
        dateKey: day.dateKey,
        weekDay: day.weekDay,
        weekIndex: isBefore(day.date, referenceMonday) ? 0 : differenceInWeeks(day.date, referenceMonday),
    }));
    return daysWithWeekIndex;
};

const groupDaysByWeekday = (days: Array<DayWithWeekInfo>): Record<number, Array<DayWithWeekInfo>> => {
    const grouped: Record<number, Array<DayWithWeekInfo>> = {};

    days.forEach((day) => {
        if (!grouped[day.weekDay]) {
            grouped[day.weekDay] = [];
        }
        grouped[day.weekDay].push(day);
    });

    return grouped;
};

type CheckResult = {
    isAvailableNow: boolean;
    isFutureAvailable: boolean;
    futureAvailableFromDate: string | null;
    timeOffDates: Array<string>;
    firstAvailableDate: string;
};

/**
 * A slot is "free" (reservable) on a date when BOTH a reservation lane and a device are free — i.e.
 * max(reservations, sessions) < capacity (capacity 1 for PSY, the clinic's device count for NFB, 0 if
 * blocked). One-off sessions don't consume a reservation lane, so they only bind via the device count.
 * The slot is "available now" only if it is free from the first date AND stays free across the window,
 * and the service genuinely works at that time (availability). Time-off days warn but don't block.
 */
const checkSlotAvailability = (
    dates: Array<DayWithWeekInfo>,
    time: string,
    checker: SlotChecker,
    filterWeekIndices?: "even" | "odd"
): CheckResult => {
    const filteredDates = filterWeekIndices
        ? dates.filter((d) => (filterWeekIndices === "even" ? d.weekIndex % 2 === 0 : d.weekIndex % 2 === 1))
        : dates;

    const emptyResult: CheckResult = {
        isAvailableNow: false,
        isFutureAvailable: false,
        futureAvailableFromDate: null,
        timeOffDates: [],
        firstAvailableDate: filteredDates[0]?.dateKey ?? "",
    };

    if (filteredDates.length === 0) {
        return emptyResult;
    }

    const isFreeOnDate = (day: DayWithWeekInfo) => {
        const occupancy = checker.getOccupancy(day.dateKey, time, checker.service);
        const capacity = checker.capacityFor(day.dateKey, time);
        const isFree = capacity > 0 && occupancy < capacity;
        return isFree;
    };

    let firstFreeIndex: number | null = null;

    for (let i = 0; i < filteredDates.length; i++) {
        if (isFreeOnDate(filteredDates[i])) {
            firstFreeIndex = i;
            break;
        }
    }

    if (firstFreeIndex === null) {
        return emptyResult;
    }

    const remainingDates = filteredDates.slice(firstFreeIndex);
    const timeOffDates: Array<string> = [];
    let hasAvailabilityData = false;
    let hasOccupiedAfterFreeStart = false;

    for (const day of remainingDates) {
        if (!isFreeOnDate(day)) {
            hasOccupiedAfterFreeStart = true;
            break;
        }

        const availabilityStatus = checker.getAvailabilityStatus(day.dateKey, time, checker.service);
        if (availabilityStatus === "time_off") {
            timeOffDates.push(day.dateKey);
        } else if (availabilityStatus === "available") {
            hasAvailabilityData = true;
        }
    }

    if (hasOccupiedAfterFreeStart || !hasAvailabilityData) {
        return emptyResult;
    }

    const isAvailableNow = firstFreeIndex === 0;
    const isFutureAvailable = firstFreeIndex > 0;
    const futureAvailableFromDate = isFutureAvailable ? filteredDates[firstFreeIndex].dateKey : null;

    return {
        isAvailableNow,
        isFutureAvailable,
        futureAvailableFromDate,
        timeOffDates,
        firstAvailableDate: filteredDates[firstFreeIndex].dateKey,
    };
};

const emptyServiceAvailability = (startDate: Date): ServiceAvailability => {
    const firstMonday = isMonday(startDate) ? startDate : nextMonday(startDate);
    return {
        weekly: [],
        biweeklyA: [],
        biweeklyB: [],
        futureWeekly: [],
        futureBiweeklyA: [],
        futureBiweeklyB: [],
        biweeklyAStartDate: format(firstMonday, "dd/MM/yyyy"),
        biweeklyBStartDate: format(addDays(firstMonday, 7), "dd/MM/yyyy"),
    };
};

const computeServiceAvailability = (
    days: Array<DayWithWeekInfo>,
    startDate: Date,
    checker: SlotChecker,
    timesForWeekDay: (weekDay: number) => Array<string>,
    includeBiweekly: boolean
): ServiceAvailability => {
    const daysByWeekday = groupDaysByWeekday(days);

    const weekly: Array<AvailableSlot> = [];
    const biweeklyA: Array<AvailableSlot> = [];
    const biweeklyB: Array<AvailableSlot> = [];
    const futureWeekly: Array<FutureAvailableSlot> = [];
    const futureBiweeklyA: Array<FutureAvailableSlot> = [];
    const futureBiweeklyB: Array<FutureAvailableSlot> = [];

    const weekdays = [1, 2, 3, 4, 5, 6];

    weekdays.forEach((weekDay) => {
        const datesForWeekday = daysByWeekday[weekDay] ?? [];
        if (datesForWeekday.length === 0) return;

        timesForWeekDay(weekDay).forEach((time) => {
            const weeklyResult = checkSlotAvailability(datesForWeekday, time, checker);
            if (weeklyResult.isAvailableNow) {
                weekly.push({
                    weekDay,
                    weekDayLabel: WEEK_DAY_SHORT_LABELS[weekDay],
                    time,
                    timeOffDates: weeklyResult.timeOffDates,
                    firstAvailableDate: weeklyResult.firstAvailableDate,
                });
            } else if (weeklyResult.isFutureAvailable && weeklyResult.futureAvailableFromDate) {
                futureWeekly.push({
                    weekDay,
                    weekDayLabel: WEEK_DAY_SHORT_LABELS[weekDay],
                    time,
                    timeOffDates: weeklyResult.timeOffDates,
                    firstAvailableDate: weeklyResult.firstAvailableDate,
                    availableFromDate: weeklyResult.futureAvailableFromDate,
                });
            }

            // NFB is never biweekly — only PSY computes the quinzenal patterns.
            if (!includeBiweekly) return;

            const evenWeekDates = datesForWeekday.filter((d) => d.weekIndex % 2 === 0);
            const biweeklyAResult = checkSlotAvailability(datesForWeekday, time, checker, "even");
            if (biweeklyAResult.isAvailableNow) {
                biweeklyA.push({
                    weekDay,
                    weekDayLabel: WEEK_DAY_SHORT_LABELS[weekDay],
                    time,
                    timeOffDates: biweeklyAResult.timeOffDates,
                    firstAvailableDate: evenWeekDates[0]?.dateKey ?? datesForWeekday[0].dateKey,
                });
            } else if (biweeklyAResult.isFutureAvailable && biweeklyAResult.futureAvailableFromDate) {
                futureBiweeklyA.push({
                    weekDay,
                    weekDayLabel: WEEK_DAY_SHORT_LABELS[weekDay],
                    time,
                    timeOffDates: biweeklyAResult.timeOffDates,
                    firstAvailableDate: biweeklyAResult.firstAvailableDate,
                    availableFromDate: biweeklyAResult.futureAvailableFromDate,
                });
            }

            const oddWeekDates = datesForWeekday.filter((d) => d.weekIndex % 2 === 1);
            const biweeklyBResult = checkSlotAvailability(datesForWeekday, time, checker, "odd");
            if (biweeklyBResult.isAvailableNow) {
                biweeklyB.push({
                    weekDay,
                    weekDayLabel: WEEK_DAY_SHORT_LABELS[weekDay],
                    time,
                    timeOffDates: biweeklyBResult.timeOffDates,
                    firstAvailableDate: oddWeekDates[0]?.dateKey ?? datesForWeekday[0].dateKey,
                });
            } else if (biweeklyBResult.isFutureAvailable && biweeklyBResult.futureAvailableFromDate) {
                futureBiweeklyB.push({
                    weekDay,
                    weekDayLabel: WEEK_DAY_SHORT_LABELS[weekDay],
                    time,
                    timeOffDates: biweeklyBResult.timeOffDates,
                    firstAvailableDate: biweeklyBResult.firstAvailableDate,
                    availableFromDate: biweeklyBResult.futureAvailableFromDate,
                });
            }
        });
    });

    const firstMonday = isMonday(startDate) ? startDate : nextMonday(startDate);

    return {
        weekly,
        biweeklyA,
        biweeklyB,
        futureWeekly,
        futureBiweeklyA,
        futureBiweeklyB,
        biweeklyAStartDate: format(firstMonday, "dd/MM/yyyy"),
        biweeklyBStartDate: format(addDays(firstMonday, 7), "dd/MM/yyyy"),
    };
};

export const findAvailableSlots = ({
    startDate,
    includePsy,
    includeNfb,
    getOccupancy,
    getNfbCapacity,
    getAvailabilityStatus,
}: FindAvailableSlotsParams): FindAvailableSlotsResult => {
    const days = generateDaysWithWeekIndex(startDate, DAYS_WINDOW);

    if (days.length === 0) {
        return { psy: emptyServiceAvailability(startDate), nfb: emptyServiceAvailability(startDate) };
    }

    const psy = includePsy
        ? computeServiceAvailability(
              days,
              startDate,
              {
                  service: "psy",
                  capacityFor: () => 1,
                  getOccupancy,
                  getAvailabilityStatus,
              },
              () => PSY_HOURLY_SLOTS,
              true
          )
        : emptyServiceAvailability(startDate);

    const nfb = includeNfb
        ? computeServiceAvailability(
              days,
              startDate,
              {
                  service: "nfb",
                  capacityFor: getNfbCapacity,
                  getOccupancy,
                  getAvailabilityStatus,
              },
              (weekDay) => (weekDay === SATURDAY_WEEKDAY ? NFB_SATURDAY_SLOTS : NFB_WEEKDAY_SLOTS),
              false
          )
        : emptyServiceAvailability(startDate);

    return { psy, nfb };
};
